import React, { useState, useEffect } from 'react';
import { adminService, authService } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';
import { HiOutlineEye, HiOutlineArrowPath } from 'react-icons/hi2';
import './MessageQueue.css';

const MessageQueue = () => {
  const { hasPermission } = usePermissions();
  const tenantId = authService.getTenantId();
  
  // Permission checks
  const canView = hasPermission('MessageQueue.View');
  const canManage = hasPermission('MessageQueue.Manage');

  console.log('canView', canView);
  console.log('canManage', canManage);
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    tenantId: null,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    if (tenantId && canView) {
      loadMessages();
    } else if (!canView) {
      setError('You do not have permission to view message queue');
      setLoading(false);
    } else {
      setError('Please login to view message queue');
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, filters.status, tenantId, canView]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      };
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.tenantId) {
        params.tenantId = filters.tenantId;
      }

      const data = await adminService.getMessageQueue(params);
      setMessages(data.data);
      setPagination({
        ...pagination,
        totalCount: data.pagination.totalCount,
        totalPages: data.pagination.totalPages,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load messages');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (id) => {
    if (!canManage) {
      setError('You do not have permission to manage message queue');
      return;
    }
    
    try {
      await adminService.retryMessage(id);
      await loadMessages();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to retry message');
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const message = await adminService.getMessage(id);
      setSelectedMessage(message);
      setShowDetailsModal(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load message details');
    }
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  const handlePageSizeChange = (pageSize) => {
    setPagination({ ...pagination, pageSize, currentPage: 1 });
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent':
        return 'status-sent';
      case 'pending':
        return 'status-pending';
      case 'failed':
        return 'status-failed';
      case 'retrying':
        return 'status-retrying';
      default:
        return 'status-unknown';
    }
  };

  if (loading && messages.length === 0) {
    return <PageLoading message="Loading messages..." />;
  }

  if (!canView) {
    return (
      <div className="message-queue-container">
        <div className="error-message" style={{ 
          textAlign: 'center', 
          padding: '2rem',
          fontSize: '1.1rem',
          color: '#d93025'
        }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view message queue</p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-queue-container">
      <div className="page-header">
        <h1>Message Queue</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filters-section">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => {
              setFilters({ ...filters, status: e.target.value });
              setPagination({ ...pagination, currentPage: 1 });
            }}
          >
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Sent">Sent</option>
            <option value="Failed">Failed</option>
            <option value="Retrying">Retrying</option>
          </select>
        </div>
      </div>

      <div className="messages-table-container">
        <table className="messages-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Template Code</th>
              <th>Recipient</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Retry Count</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-state">
                  No messages found.
                </td>
              </tr>
            ) : (
              messages.map((message) => (
                <tr key={message.messageId}>
                  <td>{message.messageId}</td>
                  <td>{message.templateCode}</td>
                  <td>{message.recipientEmail}</td>
                  <td className="subject-cell">{message.subject}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(message.status)}`}>
                      {message.status}
                    </span>
                  </td>
                  <td>{message.retryCount} / {message.maxRetries}</td>
                  <td>{new Date(message.createdAt).toLocaleString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-view"
                        onClick={() => handleViewDetails(message.messageId)}
                        title="View Details"
                      >
                        <HiOutlineEye size={18} />
                      </button>
                      {(message.status === 'Failed' || message.status === 'Retrying') && canManage && (
                        <button
                          className="btn-icon-retry"
                          onClick={() => handleRetry(message.messageId)}
                          title="Retry Message"
                        >
                          <HiOutlineArrowPath size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.totalCount > 0 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <SharedModal
        isOpen={showDetailsModal && !!selectedMessage}
        onClose={() => setShowDetailsModal(false)}
        title="Message Details"
        size="lg"
        footer={
          <>
            {(selectedMessage?.status === 'Failed' || selectedMessage?.status === 'Retrying') && canManage && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleRetry(selectedMessage.messageId);
                  setShowDetailsModal(false);
                }}
              >
                Retry
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>
              Close
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <div className="message-details">
            <div className="detail-row">
              <label>Message ID:</label>
              <span>{selectedMessage?.messageId}</span>
            </div>
            <div className="detail-row">
              <label>Template Code:</label>
              <span>{selectedMessage?.templateCode}</span>
            </div>
            <div className="detail-row">
              <label>Recipient:</label>
              <span>{selectedMessage?.recipientEmail} ({selectedMessage?.recipientName})</span>
            </div>
            <div className="detail-row">
              <label>Subject:</label>
              <span>{selectedMessage?.subject}</span>
            </div>
            <div className="detail-row">
              <label>Status:</label>
              <span className={`status-badge ${getStatusBadgeClass(selectedMessage?.status)}`}>
                {selectedMessage?.status}
              </span>
            </div>
            <div className="detail-row">
              <label>Retry Count:</label>
              <span>{selectedMessage?.retryCount} / {selectedMessage?.maxRetries}</span>
            </div>
            {selectedMessage?.errorMessage && (
              <div className="detail-row">
                <label>Error:</label>
                <span className="error-text">{selectedMessage?.errorMessage}</span>
              </div>
            )}
            <div className="detail-row">
              <label>Created At:</label>
              <span>{new Date(selectedMessage?.createdAt).toLocaleString()}</span>
            </div>
            {selectedMessage?.sentAt && (
              <div className="detail-row">
                <label>Sent At:</label>
                <span>{new Date(selectedMessage?.sentAt).toLocaleString()}</span>
              </div>
            )}
            <div className="detail-row full-width">
              <label>Body:</label>
              <div
                className="message-body"
                dangerouslySetInnerHTML={{ __html: selectedMessage?.body }}
              />
            </div>
          </div>
        </div>
      </SharedModal>
    </div>
  );
};

export default MessageQueue;

